import React, { useCallback, useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { Activity, ArrowDown, ArrowUp, ArrowUpDown, Ban, CalendarClock, ChevronDown, ChevronRight, Gift, Loader2, Pencil, Percent, Plus, RotateCcw, Search, ShieldAlert, Signal, Trash2, Unplug, Users } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import ParticleService from '../ParticleService';
import { billingIntervalLabel, formatSubscriptionDate as formatDate } from '../subscriptionPresentation';

const formatDateTime = value => value ? new Date(value).toLocaleString() : '—';
const toDateInput = value => value ? new Date(value).toISOString().slice(0, 10) : '';
const utcDate = value => `${value}T00:00:00Z`;
const productLabel = value => String(value) === String(ParticleService.LTEProductID) ? 'LTE' : String(value) === String(ParticleService.WIFIProductID) ? 'Wi-Fi' : `Product ${value}`;
const currentEntitlementFor = device => device?.entitlements?.find(entitlement => entitlement.id === device.currentEntitlementId) || device?.entitlements?.[0];
const paidSubscriptionMeta = subscription => {
  if (!subscription) return { label: 'Not subscribed', color: 'secondary' };
  if (subscription.status === 'Scheduled') return { label: 'Renewal scheduled', color: 'primary' };
  if (subscription.status === 'RenewalCancelled') return { label: 'Renewal cancelled', color: 'secondary' };
  if (subscription.status === 'Active') return { label: 'Subscribed', color: 'success' };
  if (['PastDue', 'GracePeriod'].includes(subscription.status)) return { label: 'Payment attention needed', color: 'warning' };
  return { label: `Subscription ${String(subscription.status).toLowerCase()}`, color: 'secondary' };
};
const accessLabel = entitlement => {
  if (!entitlement) return 'Access not configured';
  if (entitlement.status === 'FreePeriod') return 'Free access active';
  if (entitlement.status === 'Active') return 'Access active';
  if (entitlement.status === 'GracePeriod') return 'In grace period';
  if (['Suspended', 'Expired', 'Cancelled'].includes(entitlement.status)) return 'Expired — not enforced';
  return `Access ${entitlement.status.toLowerCase()}`;
};

const entitlementStatusLabel = status => ({
  Active: 'Access active',
  FreePeriod: 'Free access active',
  GracePeriod: 'In grace period',
  PastDue: 'Payment past due',
  Suspended: 'Access expired',
  Expired: 'Access expired',
  Cancelled: 'Access ended',
  Pending: 'Access scheduled',
  Mixed: 'Mixed access',
  NotConfigured: 'Not configured',
}[status] || status || 'Not configured');

const statusVariant = status => {
  if (['Active', 'FreePeriod'].includes(status)) return 'success';
  if (['PastDue', 'GracePeriod', 'Pending'].includes(status)) return 'warning';
  if (['Suspended', 'Expired', 'Cancelled', 'Disabled'].includes(status)) return 'danger';
  return 'secondary';
};

const salesStatusMeta = {
  Enforced: { label: 'Device restricted', color: 'danger', hint: 'One or more devices has restricted service' },
  EnforcementDue: { label: 'Expired — action due', color: 'danger', hint: 'Expired or cancelled, but not blocked' },
  PaymentDue: { label: 'Payment due / grace', color: 'warning', hint: 'Payment follow-up is required' },
  RenewalSoon: { label: 'Renewal due soon', color: 'info', hint: 'Access ends within 30 days' },
  RenewalMissing: { label: 'No renewal scheduled', color: 'primary', hint: 'Access is current but future billing is not arranged' },
  Healthy: { label: 'Good standing', color: 'success', hint: 'Current access and future billing are covered' },
  SetupNeeded: { label: 'Setup incomplete', color: 'primary', hint: 'One or more devices does not have current access' },
  AwaitingActivation: { label: 'Awaiting first login', color: 'info', hint: 'Fully configured and ready for customer activation' },
  ReviewRequired: { label: 'Review required', color: 'dark', hint: 'Mixed or unexpected account state' },
  NoDevices: { label: 'No devices', color: 'secondary', hint: 'Customer has no assigned devices' },
};

const salesSummaryCards = ['Enforced', 'EnforcementDue', 'PaymentDue', 'SetupNeeded', 'ReviewRequired', 'RenewalSoon', 'RenewalMissing', 'Healthy', 'NoDevices'];

const renewalVariant = status => ({
  Active: 'success', Scheduled: 'success', PaymentAttention: 'warning', Partial: 'info',
  NotScheduled: 'primary', Cancelled: 'secondary', Mixed: 'dark', Review: 'dark', NotApplicable: 'secondary',
}[status] || 'secondary');

const defaultGrantDates = () => {
  const starts = new Date();
  const ends = new Date(starts);
  ends.setUTCFullYear(ends.getUTCFullYear() + 1);
  return { startsAt: toDateInput(starts), endsAt: toDateInput(ends), reason: '' };
};

const defaultDiscount = () => ({
  kind: 'Percentage', value: '', currency: 'CAD', billingInterval: '',
  startsAt: toDateInput(new Date()), endsAt: '', reason: '',
});

const discountLabel = discount => {
  if (discount.kind === 'Percentage') return `${discount.value}% off`;
  if (discount.kind === 'FixedAmount') return `${discount.currency} ${discount.value} off`;
  return `${discount.currency} ${discount.value} fixed price`;
};

const SortHeading = ({ label, field, sort, onSort }) => {
  const active = sort.field === field;
  const Icon = active ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className="btn btn-link p-0 text-dark fw-bold text-decoration-none d-inline-flex align-items-center gap-1" onClick={() => onSort(field)}>
        {label}<Icon size={14} className={active ? '' : 'text-muted'} />
      </button>
    </th>
  );
};

export default function CustomerManagement({
  title = 'Customer Management',
  description = 'Customers, assigned devices, and dashboard access in one place.',
  initialSortField = 'customer',
}) {
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [filters, setFilters] = useState({ accountStatus: '', accessStatus: '', deviceCoverage: '', renewalStatus: '', salesStatus: '' });
  const [sort, setSort] = useState({ field: initialSortField, direction: 'asc' });
  const [registry, setRegistry] = useState({ items: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editor, setEditor] = useState(null);
  const [entitlementForm, setEntitlementForm] = useState(defaultGrantDates());
  const [cancellationEditor, setCancellationEditor] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancellationAcknowledged, setCancellationAcknowledged] = useState(false);
  const [discountEditor, setDiscountEditor] = useState(null);
  const [discountForm, setDiscountForm] = useState(defaultDiscount());
  const [saving, setSaving] = useState(false);
  const [simStatuses, setSimStatuses] = useState({});
  const [simProcessing, setSimProcessing] = useState(null);
  const [unclaimingDevice, setUnclaimingDevice] = useState(null);
  const [expandedDevices, setExpandedDevices] = useState(new Set());
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [enforcementEditor, setEnforcementEditor] = useState(null);
  const [enforcementReason, setEnforcementReason] = useState('');
  const [enforcementConfirmation, setEnforcementConfirmation] = useState('');
  const [restoreEditor, setRestoreEditor] = useState(null);
  const [restoreForm, setRestoreForm] = useState({ ...defaultGrantDates(), confirmation: '' });

  const loadRegistry = useCallback(async (page = 1) => {
    setLoading(true);
    const result = await ParticleService.getCustomerRegistry(submittedSearch, page, {
      ...filters,
      sortBy: sort.field,
      sortDirection: sort.direction,
    });
    if (result.error) toast.error(result.error);
    else setRegistry(result);
    setLoading(false);
  }, [submittedSearch, filters, sort]);

  useEffect(() => { loadRegistry(1); }, [loadRegistry]);

  const loadSimStatuses = async devices => {
    const entries = await Promise.all((devices || []).map(async device => {
      if (!device.iccid) return [device.id, 'unavailable'];
      try {
        const result = await ParticleService.getSim(device.iccid);
        return [device.id, result?.status || 'unknown'];
      } catch {
        return [device.id, 'unknown'];
      }
    }));
    setSimStatuses(Object.fromEntries(entries));
  };

  const loadCustomer = async (customerId, fallbackEmail = '') => {
    setDetailLoading(true);
    if (!selected) setSelected({ email: fallbackEmail });
    const result = await ParticleService.getCustomerProfile(customerId);
    if (result.error) {
      toast.error(result.error);
      setSelected(null);
    } else {
      setSelected(result);
      loadSimStatuses(result.devices);
    }
    setDetailLoading(false);
  };

  const openCustomer = customer => {
    setSimStatuses({});
    setExpandedDevices(new Set());
    loadCustomer(customer.id, customer.email);
  };

  const toggleDevice = id => setExpandedDevices(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const deleteCustomerAccount = async () => {
    if (deleteConfirmation !== 'DELETE' || !selected?.email) return;
    setDeletingAccount(true);
    try {
      const result = await ParticleService.deleteUser(selected.email);
      if (result.error) {
        toast.error(result.detail || result.error);
        return;
      }
      await ParticleService.synchronizeCustomerRegistry();
      toast.success(`${selected.email} can no longer sign in. The Particle customer account was permanently deleted.`);
      setDeleteAccountOpen(false);
      setDeleteConfirmation('');
      setSelected(null);
      await loadRegistry(registry.page);
    } catch (error) {
      console.error(error);
      toast.error('The customer account could not be deleted.');
    } finally {
      setDeletingAccount(false);
    }
  };

  const openEnforcement = (customer, device) => {
    setEnforcementEditor({ customer, device });
    setEnforcementReason('');
    setEnforcementConfirmation('');
  };

  const enforceCustomer = async event => {
    event.preventDefault();
    if (enforcementReason.trim().length < 5 || enforcementConfirmation !== 'ENFORCE') return;
    setSaving(true);
    const result = await ParticleService.enforceCustomerAccess(enforcementEditor.customer.id, enforcementEditor.device.id, enforcementReason.trim(), enforcementConfirmation);
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(result.warning ? `Device restricted. SIM warning: ${result.warning}` : 'This device is restricted and its SIM was deactivated.');
      setEnforcementEditor(null);
      if (selected?.id === enforcementEditor.customer.id) await loadCustomer(selected.id, selected.email);
      await loadRegistry(registry.page);
    }
    setSaving(false);
  };

  const openRestore = (customer, device) => {
    setRestoreEditor({ customer, device });
    setRestoreForm({ ...defaultGrantDates(), confirmation: '' });
  };

  const restoreCustomer = async event => {
    event.preventDefault();
    if (restoreForm.reason.trim().length < 5 || restoreForm.confirmation !== 'RESTORE') return;
    setSaving(true);
    const result = await ParticleService.restoreCustomerAccess(restoreEditor.customer.id, restoreEditor.device.id, {
      startsAtUtc: utcDate(restoreForm.startsAt),
      endsAtUtc: utcDate(restoreForm.endsAt),
      reason: restoreForm.reason.trim(),
      confirmation: restoreForm.confirmation,
    });
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(result.warning ? `Device access restored. SIM warning: ${result.warning}` : 'Device access was restored with the new dates and its SIM was reactivated.');
      setRestoreEditor(null);
      if (selected?.id === restoreEditor.customer.id) await loadCustomer(selected.id, selected.email);
      await loadRegistry(registry.page);
    }
    setSaving(false);
  };

  const updateSim = async (device, action) => {
    if (action === 'deactivate') {
      const confirmation = window.prompt(`Type DEACTIVATE to pause the SIM for ${device.displayName || device.particleDeviceId}.`);
      if (confirmation !== 'DEACTIVATE') return;
    }
    setSimProcessing(device.id);
    setSimStatuses(current => ({ ...current, [device.id]: action === 'deactivate' ? 'deactivating' : 'activating' }));
    try {
      const result = await ParticleService.manageSim(device.iccid, action);
      if (result.error) {
        toast.error(result.detail || result.error);
        await loadSimStatuses(selected.devices);
      } else {
        setSimStatuses(current => ({ ...current, [device.id]: action === 'deactivate' ? 'deactivated' : 'active' }));
        toast.success(`SIM ${action === 'deactivate' ? 'deactivation' : 'reactivation'} requested. Particle may take up to 30 minutes to reflect the change.`);
      }
    } catch (error) {
      console.error(error);
      toast.error('The SIM command could not be sent.');
      await loadSimStatuses(selected.devices);
    } finally {
      setSimProcessing(null);
    }
  };

  const renderSimControl = device => {
    if (!device.iccid) return <span className="text-muted">No SIM</span>;
    const status = (simStatuses[device.id] || 'loading').toLowerCase();
    const transitioning = status === 'activating' || status === 'deactivating' || simProcessing === device.id;
    const inactive = status.includes('inactive') || status.includes('deactivated');
    return (
      <div className="d-flex flex-column align-items-start gap-1">
        <Badge bg={inactive ? 'danger' : status === 'active' ? 'success' : 'secondary'}>
          {transitioning ? <Loader2 size={12} className="spin-anim me-1" /> : <Signal size={12} className="me-1" />}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
        <Button
          size="sm"
          variant={inactive ? 'outline-success' : 'outline-danger'}
          disabled={transitioning || status === 'loading'}
          onClick={() => updateSim(device, inactive ? 'reactivate' : 'deactivate')}
        >
          {inactive ? <><Activity size={13} className="me-1" />Reactivate</> : <><Ban size={13} className="me-1" />Deactivate</>}
        </Button>
      </div>
    );
  };

  const unclaimDevice = async device => {
    const label = device.displayName || device.particleDeviceId;
    const confirmation = window.prompt(
      `Unclaim ${label} from ${selected.email}?\n\n` +
      'Only do this when the physical device is no longer with this customer. ' +
      'If they still have the physical device, they can reclaim it at any time.\n\n' +
      'Type UNCLAIM to remove the current owner.'
    );
    if (confirmation !== 'UNCLAIM') return;

    setUnclaimingDevice(device.id);
    try {
      const result = await ParticleService.unclaimProductDevice(device.particleProductId, device.particleDeviceId);
      if (result.error) {
        toast.error(result.detail || result.error);
        return;
      }
      toast.success(`${label} is now unclaimed in Particle.`);
      const synchronization = await ParticleService.synchronizeCustomerRegistry();
      if (synchronization.error) {
        toast.warning('Particle removed the owner, but the local customer registry could not be refreshed yet.');
      }
      await loadCustomer(selected.id, selected.email);
      await loadRegistry(registry.page);
    } catch (error) {
      console.error(error);
      toast.error('The device could not be unclaimed.');
    } finally {
      setUnclaimingDevice(null);
    }
  };

  const openEntitlementEditor = (device, entitlement = null) => {
    setEditor({ device, entitlement });
    setEntitlementForm(entitlement ? {
      startsAt: toDateInput(entitlement.startsAtUtc),
      endsAt: toDateInput(entitlement.endsAtUtc),
      reason: '',
    } : defaultGrantDates());
  };

  const saveEntitlement = async event => {
    event.preventDefault();
    if (!entitlementForm.startsAt || !entitlementForm.endsAt || entitlementForm.reason.trim().length < 5) {
      toast.error('Start date, end date, and a reason of at least 5 characters are required.');
      return;
    }
    setSaving(true);
    const body = {
      startsAtUtc: utcDate(entitlementForm.startsAt),
      endsAtUtc: utcDate(entitlementForm.endsAt),
      reason: entitlementForm.reason.trim(),
    };
    const result = editor.entitlement
      ? await ParticleService.updateCustomerEntitlement(selected.id, editor.entitlement.id, body)
      : await ParticleService.grantComplimentaryEntitlement(selected.id, editor.device.id, body);
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(editor.entitlement ? 'Entitlement dates updated.' : 'Complimentary entitlement granted.');
      setEditor(null);
      await loadCustomer(selected.id, selected.email);
      await loadRegistry(registry.page);
    }
    setSaving(false);
  };

  const openCancellation = (device, entitlement) => {
    setCancellationEditor({ device, entitlement });
    setCancellationReason('');
    setCancellationAcknowledged(false);
  };

  const cancelSubscription = async event => {
    event.preventDefault();
    if (cancellationReason.trim().length < 5) {
      toast.error('A cancellation reason of at least 5 characters is required.');
      return;
    }
    if (!cancellationAcknowledged) {
      toast.error('Confirm that you understand this does not issue a refund.');
      return;
    }
    setSaving(true);
    const result = await ParticleService.cancelCustomerSubscription(
      selected.id, cancellationEditor.entitlement.id, cancellationReason.trim());
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(result.providerRecordAlreadyAbsent
        ? 'Renewal was already absent in Helcim; Cannon Control is now reconciled.'
        : 'Recurring renewal cancelled in Helcim.');
      setCancellationEditor(null);
      await loadCustomer(selected.id, selected.email);
      await loadRegistry(registry.page);
    }
    setSaving(false);
  };

  const openDiscountEditor = (discount = null, ending = false) => {
    setDiscountEditor({ discount, ending });
    setDiscountForm(ending ? { ...defaultDiscount(), reason: '' } : discount ? {
      kind: discount.kind,
      value: discount.value,
      currency: discount.currency || 'CAD',
      billingInterval: discount.billingInterval || '',
      startsAt: toDateInput(discount.startsAtUtc),
      endsAt: toDateInput(discount.endsAtUtc),
      reason: discount.reason,
    } : defaultDiscount());
  };

  const saveDiscount = async event => {
    event.preventDefault();
    if (discountForm.reason.trim().length < 5) {
      toast.error('A reason of at least 5 characters is required.');
      return;
    }
    setSaving(true);
    let result;
    if (discountEditor.ending) {
      result = await ParticleService.endCustomerDiscount(selected.id, discountEditor.discount.id, discountForm.reason.trim());
    } else {
      const body = {
        kind: discountForm.kind,
        value: Number(discountForm.value),
        currency: discountForm.kind === 'Percentage' ? null : discountForm.currency,
        billingInterval: discountForm.billingInterval || null,
        startsAtUtc: utcDate(discountForm.startsAt),
        endsAtUtc: discountForm.endsAt ? utcDate(discountForm.endsAt) : null,
        reason: discountForm.reason.trim(),
      };
      result = discountEditor.discount
        ? await ParticleService.updateCustomerDiscount(selected.id, discountEditor.discount.id, body)
        : await ParticleService.createCustomerDiscount(selected.id, body);
    }
    if (result.error) toast.error(result.detail || result.error);
    else {
      toast.success(discountEditor.ending ? 'Discount ended.' : discountEditor.discount ? 'Discount updated.' : 'Discount created.');
      setDiscountEditor(null);
      await loadCustomer(selected.id, selected.email);
    }
    setSaving(false);
  };

  const submitSearch = event => {
    event.preventDefault();
    setSubmittedSearch(search.trim());
  };

  const updateFilter = (name, value) => setFilters(current => ({ ...current, [name]: value }));

  const toggleSort = field => setSort(current => ({
    field,
    direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
  }));

  const clearFilters = () => {
    setSearch('');
    setSubmittedSearch('');
    setFilters({ accountStatus: '', accessStatus: '', deviceCoverage: '', renewalStatus: '', salesStatus: '' });
    setSort({ field: initialSortField, direction: 'asc' });
  };

  const hasFilters = search || submittedSearch || filters.accountStatus || filters.accessStatus || filters.deviceCoverage || filters.renewalStatus || filters.salesStatus || sort.field !== initialSortField || sort.direction !== 'asc';

  return (
    <div className="container-fluid p-4">
      <div className="mb-4">
        <h2 className="mb-1 d-flex align-items-center gap-2"><Users size={25} /> {title}</h2>
        <p className="text-muted mb-0">{description}</p>
      </div>

      <div className="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3 mb-4">
        {salesSummaryCards.map(code => {
          const meta = salesStatusMeta[code];
          const active = filters.salesStatus === code;
          return (
            <div className="col" key={code}>
              <button
                type="button"
                className={`card w-100 h-100 text-start shadow-sm border-${meta.color}${active ? ` border-3 bg-${meta.color} bg-opacity-10` : ''}`}
                onClick={() => updateFilter('salesStatus', active ? '' : code)}
                aria-pressed={active}
              >
                <span className="card-body p-3">
                  <span className={`fs-3 fw-bold text-${meta.color}`}>{registry.summary?.[code] || 0}</span>
                  <span className="d-block fw-semibold">{meta.label}</span>
                  <span className="d-block small text-muted mt-1">{meta.hint}</span>
                </span>
              </button>
            </div>
          );
        })}
      </div>

      <Card className="shadow-sm">
        <Card.Header className="bg-white py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
            <strong>{registry.total} customers</strong>
            <Form onSubmit={submitSearch} style={{ width: 'min(100%, 430px)' }}>
              <InputGroup>
                <Form.Control value={search} onChange={event => setSearch(event.target.value)} placeholder="Search email or name" />
                <Button type="submit" variant="outline-primary"><Search size={17} /></Button>
              </InputGroup>
            </Form>
          </div>
          <div className="d-flex flex-wrap align-items-end gap-2">
            <Form.Group style={{ minWidth: 190 }}>
              <Form.Label className="small mb-1">Sales health</Form.Label>
              <Form.Select size="sm" value={filters.salesStatus} onChange={event => updateFilter('salesStatus', event.target.value)}>
                <option value="">All sales-health statuses</option>
                {Object.entries(salesStatusMeta).map(([code, meta]) => <option value={code} key={code}>{meta.label}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 170 }}>
              <Form.Label className="small mb-1">Account</Form.Label>
              <Form.Select size="sm" value={filters.accountStatus} onChange={event => updateFilter('accountStatus', event.target.value)}>
                <option value="">All account statuses</option>
                <option value="Pending">Pending</option><option value="Active">Active</option><option value="Suspended">Suspended</option><option value="Disabled">Disabled</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="small mb-1">Current access</Form.Label>
              <Form.Select size="sm" value={filters.accessStatus} onChange={event => updateFilter('accessStatus', event.target.value)}>
                <option value="">All access statuses</option>
                <option value="Active">Access active</option><option value="FreePeriod">Free access active</option><option value="Pending">Access scheduled</option><option value="GracePeriod">In grace period</option><option value="PastDue">Payment past due</option><option value="ExpiredAccess">Access expired</option><option value="Cancelled">Access ended</option><option value="Mixed">Mixed access</option><option value="NotConfigured">Not configured</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 180 }}>
              <Form.Label className="small mb-1">Coverage</Form.Label>
              <Form.Select size="sm" value={filters.deviceCoverage} onChange={event => updateFilter('deviceCoverage', event.target.value)}>
                <option value="">All coverage</option>
                <option value="complete">All devices covered</option><option value="partial">Partially covered</option><option value="none">No current coverage</option><option value="nodevices">No devices</option>
              </Form.Select>
            </Form.Group>
            <Form.Group style={{ minWidth: 190 }}>
              <Form.Label className="small mb-1">Renewal</Form.Label>
              <Form.Select size="sm" value={filters.renewalStatus} onChange={event => updateFilter('renewalStatus', event.target.value)}>
                <option value="">All renewal states</option>
                <option value="Scheduled">Renewal scheduled</option><option value="Active">Paid subscription active</option><option value="NotScheduled">No renewal scheduled</option><option value="Partial">Partially scheduled</option><option value="PaymentAttention">Payment attention needed</option><option value="Cancelled">Renewal cancelled</option><option value="Review">Review required</option><option value="NotApplicable">Not applicable</option>
              </Form.Select>
            </Form.Group>
            <Button size="sm" variant="outline-secondary" disabled={!hasFilters} onClick={clearFilters}>Clear filters</Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /><div className="mt-2">Loading customers…</div></div>
          ) : registry.items.length === 0 ? (
            <div className="text-center text-muted p-5">No customers match this search.</div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <SortHeading label="Customer" field="customer" sort={sort} onSort={toggleSort} />
                  <SortHeading label="Sales health" field="status" sort={sort} onSort={toggleSort} />
                  <SortHeading label="Coverage" field="devices" sort={sort} onSort={toggleSort} />
                  <SortHeading label="Current access" field="access" sort={sort} onSort={toggleSort} />
                  <SortHeading label="Renewal" field="renewal" sort={sort} onSort={toggleSort} />
                  <SortHeading label="Next important date" field="expiry" sort={sort} onSort={toggleSort} />
                  <th></th>
                </tr>
              </thead>
              <tbody>{registry.items.map(customer => (
                <tr key={customer.id}>
                  <td><strong>{customer.email}</strong><div className="small text-muted">{customer.organizationName || 'Organization pending'}</div></td>
                  <td>
                    <Badge bg={salesStatusMeta[customer.salesStatus]?.color || 'secondary'}>{customer.salesStatusLabel}</Badge>
                    <div className="small text-muted mt-1" style={{ maxWidth: 220 }}>{customer.nextAction}</div>
                  </td>
                  <td>
                    <strong className={customer.deviceCount > 0 && customer.coveredDeviceCount === customer.deviceCount ? 'text-success' : ''}>{customer.coveredDeviceCount} of {customer.deviceCount} covered</strong>
                    <div className="small text-muted">Current dashboard access</div>
                  </td>
                  <td><Badge bg={statusVariant(customer.currentAccessStatus)}>{entitlementStatusLabel(customer.currentAccessStatus)}</Badge></td>
                  <td>
                    <Badge bg={renewalVariant(customer.renewalStatus)}>{customer.renewalLabel}</Badge>
                    <div className="small text-muted mt-1" style={{ maxWidth: 260 }}>{customer.renewalDetail}</div>
                  </td>
                  <td>{customer.nextImportantAtUtc ? <><div>{formatDate(customer.nextImportantAtUtc)}</div><div className="small text-muted">{customer.nextImportantLabel}</div></> : <span className="text-muted">—</span>}</td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" onClick={() => openCustomer(customer)}>Manage</Button>
                  </td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </Card.Body>
        {registry.totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <Button variant="outline-secondary" size="sm" disabled={registry.page <= 1} onClick={() => loadRegistry(registry.page - 1)}>Previous</Button>
            <span className="small text-muted">Page {registry.page} of {registry.totalPages}</span>
            <Button variant="outline-secondary" size="sm" disabled={registry.page >= registry.totalPages} onClick={() => loadRegistry(registry.page + 1)}>Next</Button>
          </Card.Footer>
        )}
      </Card>

      <Modal show={selected !== null && editor === null && cancellationEditor === null && discountEditor === null && enforcementEditor === null && restoreEditor === null && !deleteAccountOpen} onHide={() => setSelected(null)} size="xl" centered scrollable>
        <Modal.Header closeButton><Modal.Title>{selected?.email || 'Customer profile'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {detailLoading || !selected?.id ? <div className="text-center p-4"><Spinner animation="border" /></div> : (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge bg={statusVariant(selected.status)}>Account: {selected.status}</Badge>
                <Badge bg="secondary">Last login: {formatDateTime(selected.lastLoginAtUtc)}</Badge>
                <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => { setDeleteConfirmation(''); setDeleteAccountOpen(true); }}>
                  <Trash2 size={14} className="me-1" /> Delete user account
                </Button>
              </div>

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h5 className="mb-0">Devices and dashboard access</h5>
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg="light" text="dark" className="border px-3 py-2">{selected.devices?.length || 0} device{selected.devices?.length === 1 ? '' : 's'}</Badge>
                  <Badge bg={(selected.devices || []).filter(device => !device.accessRestricted && ['Active', 'FreePeriod', 'GracePeriod'].includes(currentEntitlementFor(device)?.status)).length === selected.devices?.length ? 'success' : 'warning'} className="px-3 py-2">
                    {(selected.devices || []).filter(device => !device.accessRestricted && ['Active', 'FreePeriod', 'GracePeriod'].includes(currentEntitlementFor(device)?.status)).length} with access
                  </Badge>
                  <Badge bg={(selected.devices || []).filter(device => device.subscribed).length === selected.devices?.length ? 'success' : 'secondary'} className="px-3 py-2">
                    {(selected.devices || []).filter(device => device.subscribed).length} subscribed
                  </Badge>
                  <Badge bg={(selected.devices || []).filter(device => (simStatuses[device.id] || '').toLowerCase() === 'active').length === selected.devices?.length ? 'primary' : 'warning'} className="px-3 py-2">
                    {(selected.devices || []).filter(device => (simStatuses[device.id] || '').toLowerCase() === 'active').length} SIM active
                  </Badge>
                </div>
              </div>
              {selected.devices?.length ? (
                <div className="d-grid gap-3">{selected.devices.map(device => {
                    const entitlement = currentEntitlementFor(device);
                    const paidSubscription = device.paidSubscription;
                    const paidMeta = paidSubscriptionMeta(paidSubscription);
                    const expanded = expandedDevices.has(device.id);
                    const simStatus = (simStatuses[device.id] || 'loading').toLowerCase();
                    const simInactive = simStatus.includes('inactive') || simStatus.includes('deactivated');
                    return (
                      <Card key={device.id} className="shadow-sm">
                        <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-3 py-3">
                          <div>
                            <div className="fw-semibold">{device.displayName || 'Unnamed device'}</div>
                            <div className="small font-monospace text-muted">{device.particleDeviceId}</div>
                          </div>
                          <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                            <Badge bg="light" text="dark" className="border">{productLabel(device.particleProductId)}</Badge>
                            <Badge bg={device.accessRestricted ? 'danger' : statusVariant(entitlement?.status)}>{device.accessRestricted ? 'Device restricted' : accessLabel(entitlement)}</Badge>
                            <Badge bg={paidMeta.color}>{paidMeta.label}</Badge>
                            <Badge bg={simInactive ? 'danger' : simStatus === 'active' ? 'success' : 'secondary'}>SIM {simStatus.replaceAll('_', ' ')}</Badge>
                            <span className="small text-muted d-none d-lg-inline">Last seen {formatDateTime(device.lastHeardAtUtc)}</span>
                            <Button size="sm" variant="outline-secondary" aria-expanded={expanded} onClick={() => toggleDevice(device.id)}>
                              {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />} {expanded ? 'Hide details' : 'View details'}
                            </Button>
                          </div>
                        </Card.Header>
                        {expanded && (
                        <Card.Body>
                          <div className="row g-4 align-items-start">
                            <div className="col-md-6 col-xl-3">
                              <div className="small text-uppercase text-muted fw-semibold mb-2">Device service</div>
                              <div className="small text-muted mb-1">Last heard</div>
                              <div className="mb-3">{formatDateTime(device.lastHeardAtUtc)}</div>
                              <div className="small text-muted mb-1">SIM status</div>
                              {renderSimControl(device)}
                            </div>
                            <div className="col-md-6 col-xl-5">
                              <div className="small text-uppercase text-muted fw-semibold mb-2">Dashboard access</div>
                              {entitlement ? (
                                <>
                                  <div className="fw-semibold">{entitlement.label}</div>
                                  <Badge bg={device.accessRestricted ? 'danger' : statusVariant(entitlement.status)} className="mt-1">{device.accessRestricted ? 'Device restricted' : entitlementStatusLabel(entitlement.status)}</Badge>
                                  <div className="row g-3 mt-1">
                                    <div className="col-sm-6">
                                      <div className="small text-muted">Access period</div>
                                      <div>{formatDate(entitlement.startsAtUtc)} – {formatDate(entitlement.endsAtUtc)}</div>
                                    </div>
                                    <div className="col-sm-6">
                                      <div className="small text-muted">Grace ends</div>
                                      <div>{formatDate(entitlement.graceEndsAtUtc)}</div>
                                    </div>
                                  </div>
                                  {device.entitlements.length > 1 && <div className="small text-muted mt-2">+{device.entitlements.length - 1} additional access record(s)</div>}
                                </>
                              ) : <Badge bg="secondary">Access not configured</Badge>}
                              <div className="border-top mt-3 pt-3">
                                <div className="small text-uppercase text-muted fw-semibold mb-2">Paid renewal</div>
                                <Badge bg={paidMeta.color}>{paidMeta.label}</Badge>
                                {paidSubscription ? (
                                  <>
                                    <div className="mt-2 fw-semibold">{paidSubscription.currency} {billingIntervalLabel(paidSubscription.billingInterval)} plan</div>
                                    <div className="small text-muted">
                                      {paidSubscription.status === 'Scheduled' ? 'First payment' : 'Paid period ends'}: {formatDate(paidSubscription.status === 'Scheduled' ? paidSubscription.startsAtUtc : paidSubscription.endsAtUtc)}
                                    </div>
                                  </>
                                ) : <div className="small text-muted mt-1">No recurring billing is configured for this device.</div>}
                              </div>
                            </div>
                            <div className="col-xl-4">
                              <div className="small text-uppercase text-muted fw-semibold mb-2">Actions</div>
                              <div className="d-grid gap-2">
                            {device.accessRestricted ? (
                              <Button size="sm" variant="outline-success" onClick={() => openRestore(selected, device)}><RotateCcw size={14} className="me-1" /> Restore device access</Button>
                            ) : entitlement && ['PastDue', 'GracePeriod', 'Suspended', 'Expired', 'Cancelled'].includes(entitlement.status) ? (
                              <Button size="sm" variant="outline-danger" onClick={() => openEnforcement(selected, device)}><ShieldAlert size={14} className="me-1" /> Restrict this device</Button>
                            ) : null}
                            {paidSubscription?.cancellable && (
                              <Button size="sm" variant="outline-danger" onClick={() => openCancellation(device, paidSubscription)}><Ban size={14} className="me-1" /> Cancel renewal</Button>
                            )}
                            {entitlement ? (entitlement.editable ? (
                              <Button size="sm" variant="outline-primary" onClick={() => openEntitlementEditor(device, entitlement)}><Pencil size={14} className="me-1" /> Edit dates</Button>
                            ) : (
                              !paidSubscription?.cancellable && <span className="small text-muted">No subscription action</span>
                            )) : (
                              <Button size="sm" variant="outline-success" onClick={() => openEntitlementEditor(device)}><Gift size={14} className="me-1" /> Grant access</Button>
                            )}
                                <div className="border-top pt-2 mt-1">
                                  <div className="small text-muted mb-2">Particle ownership</div>
                            <Button
                              size="sm"
                              variant="outline-danger"
                                    className="w-100"
                              disabled={unclaimingDevice === device.id}
                              onClick={() => unclaimDevice(device)}
                            >
                              <Unplug size={14} className="me-1" />
                              {unclaimingDevice === device.id ? 'Unclaiming…' : 'Unclaim device'}
                            </Button>
                                </div>
                              </div>
                          </div>
                          </div>
                        </Card.Body>
                        )}
                      </Card>
                    );
                  })}</div>
              ) : <p className="text-muted">No devices are currently assigned to this customer.</p>}

              <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                <h5 className="mb-0">Customer discounts</h5>
                <Button size="sm" variant="outline-success" onClick={() => openDiscountEditor()}><Plus size={14} className="me-1" /> Add discount</Button>
              </div>
              {selected.discounts?.length ? (
                <Table responsive hover className="align-middle border">
                  <thead className="table-light"><tr><th>Discount</th><th>Applies to</th><th>Period</th><th>Status</th><th>Reason</th><th></th></tr></thead>
                  <tbody>{selected.discounts.map(discount => (
                    <tr key={discount.id}>
                      <td><strong>{discountLabel(discount)}</strong><div className="small text-muted">{discount.scope} level</div></td>
                      <td>{discount.billingInterval || 'Monthly and annual'}</td>
                      <td>{formatDate(discount.startsAtUtc)}<div className="small text-muted">to {discount.endsAtUtc ? formatDate(discount.endsAtUtc) : 'No end date'}</div></td>
                      <td><Badge bg={statusVariant(discount.status)}>{discount.status}</Badge></td>
                      <td>{discount.reason}</td>
                      <td className="text-end text-nowrap">
                        {discount.editable && <>
                          <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openDiscountEditor(discount)}><Pencil size={13} /> Edit</Button>
                          <Button size="sm" variant="outline-danger" onClick={() => openDiscountEditor(discount, true)}><Ban size={13} /> End</Button>
                        </>}
                      </td>
                    </tr>
                  ))}</tbody>
                </Table>
              ) : <p className="text-muted">No customer-specific discounts.</p>}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={enforcementEditor !== null} onHide={() => !saving && setEnforcementEditor(null)} centered>
        <Form onSubmit={enforceCustomer}>
          <Modal.Header closeButton={!saving} className="bg-danger text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><ShieldAlert size={21} /> Restrict past-due device</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Restrict <strong>{enforcementEditor?.device?.displayName || enforcementEditor?.device?.particleDeviceId}</strong> for <strong>{enforcementEditor?.customer?.email}</strong>?</p>
            <div className="alert alert-warning">
              This blocks controls and telemetry for this device and deactivates only its cellular SIM. The customer can still sign in, review dates, pay, and use other current devices. It does <strong>not</strong> unclaim the device or alter Helcim payment history.
            </div>
            {enforcementEditor?.device?.entitlements?.[0]?.status === 'GracePeriod' && <p className="small text-danger fw-semibold">This device is still in its payment grace period. Confirm that early restriction is intended.</p>}
            <Form.Group className="mb-3">
              <Form.Label>Reason</Form.Label>
              <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} value={enforcementReason} onChange={event => setEnforcementReason(event.target.value)} placeholder="Required for audit history" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type <strong>ENFORCE</strong> to confirm</Form.Label>
              <Form.Control required value={enforcementConfirmation} onChange={event => setEnforcementConfirmation(event.target.value)} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={saving} onClick={() => setEnforcementEditor(null)}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={saving || enforcementConfirmation !== 'ENFORCE' || enforcementReason.trim().length < 5}>
              {saving ? 'Restricting…' : 'Restrict device and deactivate SIM'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={restoreEditor !== null} onHide={() => !saving && setRestoreEditor(null)} centered>
        <Form onSubmit={restoreCustomer}>
          <Modal.Header closeButton={!saving} className="bg-success text-white">
            <Modal.Title className="d-flex align-items-center gap-2"><RotateCcw size={21} /> Restore device access</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Restore <strong>{restoreEditor?.device?.displayName || restoreEditor?.device?.particleDeviceId}</strong> for <strong>{restoreEditor?.customer?.email}</strong> with a dated administrative access override.</p>
            <div className="alert alert-info">This restores controls and telemetry for this device and reactivates only its SIM. Existing Helcim and historical entitlement records remain unchanged.</div>
            <div className="row g-3 mb-3">
              <Form.Group className="col-sm-6">
                <Form.Label>New access start</Form.Label>
                <Form.Control type="date" required value={restoreForm.startsAt} onChange={event => setRestoreForm(current => ({ ...current, startsAt: event.target.value }))} />
              </Form.Group>
              <Form.Group className="col-sm-6">
                <Form.Label>New access end</Form.Label>
                <Form.Control type="date" required value={restoreForm.endsAt} onChange={event => setRestoreForm(current => ({ ...current, endsAt: event.target.value }))} />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Reason for restoring access</Form.Label>
              <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} value={restoreForm.reason} onChange={event => setRestoreForm(current => ({ ...current, reason: event.target.value }))} placeholder="Required for audit history" />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type <strong>RESTORE</strong> to confirm</Form.Label>
              <Form.Control required value={restoreForm.confirmation} onChange={event => setRestoreForm(current => ({ ...current, confirmation: event.target.value }))} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={saving} onClick={() => setRestoreEditor(null)}>Cancel</Button>
            <Button type="submit" variant="success" disabled={saving || restoreForm.confirmation !== 'RESTORE' || restoreForm.reason.trim().length < 5}>
              {saving ? 'Restoring…' : 'Save dates and restore access'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={deleteAccountOpen} onHide={() => !deletingAccount && setDeleteAccountOpen(false)} centered>
        <Modal.Header closeButton={!deletingAccount} className="bg-danger text-white">
          <Modal.Title>Delete user account permanently?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This permanently deletes the Particle customer account for <strong>{selected?.email}</strong> and disables dashboard login.</p>
          <p className="text-danger fw-semibold">This cannot be undone. Any assigned devices may become unclaimed and can be reclaimed by someone who still has the physical device.</p>
          <p className="small text-muted">Sales, payment, subscription, and audit history will be retained for business records.</p>
          <Form.Group>
            <Form.Label>Type <strong>DELETE</strong> to confirm</Form.Label>
            <Form.Control autoFocus value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} disabled={deletingAccount} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={deletingAccount} onClick={() => setDeleteAccountOpen(false)}>Cancel</Button>
          <Button variant="danger" disabled={deleteConfirmation !== 'DELETE' || deletingAccount} onClick={deleteCustomerAccount}>
            {deletingAccount ? 'Deleting…' : 'Permanently delete account'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={editor !== null} onHide={() => !saving && setEditor(null)} centered>
        <Form onSubmit={saveEntitlement}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title className="d-flex align-items-center gap-2"><CalendarClock size={21} /> {editor?.entitlement ? 'Edit entitlement dates' : 'Grant complimentary access'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3"><strong>{editor?.device.displayName || 'Device'}</strong><div className="small font-monospace text-muted">{editor?.device.particleDeviceId}</div></div>
            <Form.Group className="mb-3">
              <Form.Label>Start date</Form.Label>
              <Form.Control type="date" required value={entitlementForm.startsAt} onChange={event => setEntitlementForm(current => ({ ...current, startsAt: event.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>End date</Form.Label>
              <Form.Control type="date" required value={entitlementForm.endsAt} onChange={event => setEntitlementForm(current => ({ ...current, endsAt: event.target.value }))} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Reason for change</Form.Label>
              <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} value={entitlementForm.reason} onChange={event => setEntitlementForm(current => ({ ...current, reason: event.target.value }))} placeholder="Required for the audit history" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={saving} onClick={() => setEditor(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving…' : 'Save entitlement'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={cancellationEditor !== null} onHide={() => !saving && setCancellationEditor(null)} centered>
        <Form onSubmit={cancelSubscription}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title className="d-flex align-items-center gap-2"><Ban size={21} /> Cancel recurring renewal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <strong>{cancellationEditor?.device.displayName || 'Device'}</strong>
              <div className="small font-monospace text-muted">{cancellationEditor?.device.particleDeviceId}</div>
            </div>
            {cancellationEditor?.entitlement.status === 'Scheduled' ? (
              <p>This removes the scheduled renewal before its first payment. The customer’s current free access is not changed.</p>
            ) : (
              <p>
                This stops future automatic charges in Helcim. It does not refund a completed payment.
                Paid dashboard access remains available through <strong>{formatDate(cancellationEditor?.entitlement.endsAtUtc)}</strong>.
              </p>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Reason for cancellation</Form.Label>
              <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} value={cancellationReason} onChange={event => setCancellationReason(event.target.value)} placeholder="Required for the audit history" />
            </Form.Group>
            <Form.Check
              required
              type="checkbox"
              checked={cancellationAcknowledged}
              onChange={event => setCancellationAcknowledged(event.target.checked)}
              label="I understand that this cancels renewal and does not issue a refund."
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={saving} onClick={() => setCancellationEditor(null)}>Keep subscription</Button>
            <Button type="submit" variant="danger" disabled={saving}>{saving ? 'Cancelling…' : 'Cancel renewal'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={discountEditor !== null} onHide={() => !saving && setDiscountEditor(null)} centered>
        <Form onSubmit={saveDiscount}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title className="d-flex align-items-center gap-2">
              {discountEditor?.ending ? <Ban size={21} /> : <Percent size={21} />}
              {discountEditor?.ending ? 'End customer discount' : discountEditor?.discount ? 'Edit customer discount' : 'Add customer discount'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {discountEditor?.ending ? (
              <p>End <strong>{discountEditor.discount && discountLabel(discountEditor.discount)}</strong> now. Its history and audit records will be retained.</p>
            ) : <>
              <Form.Group className="mb-3">
                <Form.Label>Discount type</Form.Label>
                <Form.Select value={discountForm.kind} onChange={event => setDiscountForm(current => ({ ...current, kind: event.target.value }))}>
                  <option value="Percentage">Percentage off</option>
                  <option value="FixedAmount">Fixed amount off</option>
                  <option value="FixedPrice">Fixed subscription price</option>
                </Form.Select>
              </Form.Group>
              <div className="row">
                <Form.Group className="col-sm-6 mb-3">
                  <Form.Label>{discountForm.kind === 'Percentage' ? 'Percentage' : 'Amount'}</Form.Label>
                  <Form.Control type="number" required min="0.01" max={discountForm.kind === 'Percentage' ? '100' : '100000'} step="0.01" value={discountForm.value} onChange={event => setDiscountForm(current => ({ ...current, value: event.target.value }))} />
                </Form.Group>
                {discountForm.kind !== 'Percentage' && <Form.Group className="col-sm-6 mb-3">
                  <Form.Label>Currency</Form.Label>
                  <Form.Select value={discountForm.currency} onChange={event => setDiscountForm(current => ({ ...current, currency: event.target.value }))}><option value="CAD">CAD</option><option value="USD">USD</option></Form.Select>
                </Form.Group>}
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Billing interval</Form.Label>
                <Form.Select value={discountForm.billingInterval} onChange={event => setDiscountForm(current => ({ ...current, billingInterval: event.target.value }))}>
                  <option value="">Monthly and annual</option><option value="Monthly">Monthly only</option><option value="Annual">Annual only</option>
                </Form.Select>
              </Form.Group>
              <div className="row">
                <Form.Group className="col-sm-6 mb-3"><Form.Label>Start date</Form.Label><Form.Control type="date" required value={discountForm.startsAt} onChange={event => setDiscountForm(current => ({ ...current, startsAt: event.target.value }))} /></Form.Group>
                <Form.Group className="col-sm-6 mb-3"><Form.Label>End date (optional)</Form.Label><Form.Control type="date" value={discountForm.endsAt} onChange={event => setDiscountForm(current => ({ ...current, endsAt: event.target.value }))} /></Form.Group>
              </div>
            </>}
            <Form.Group>
              <Form.Label>{discountEditor?.ending ? 'Reason for ending discount' : 'Reason for discount'}</Form.Label>
              <Form.Control as="textarea" rows={3} required minLength={5} maxLength={500} value={discountForm.reason} onChange={event => setDiscountForm(current => ({ ...current, reason: event.target.value }))} placeholder="Required for the audit history" />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" disabled={saving} onClick={() => setDiscountEditor(null)}>Cancel</Button>
            <Button type="submit" variant={discountEditor?.ending ? 'danger' : 'primary'} disabled={saving}>{saving ? 'Saving…' : discountEditor?.ending ? 'End discount' : 'Save discount'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <ToastContainer />
    </div>
  );
}
